import { c as _c } from "react/compiler-runtime";
import { IcoPlusSmall16, IcoTrash16 } from '@onefootprint/icons';
import { IconButton, LinkButton, Select, TextInput } from '@onefootprint/ui';
import { useTranslation } from 'react-i18next';
import { type FieldType, isEnumType, isObjectType, type Property } from '../../../store/types';
import { newProperty } from '../../../store/utils';
import FieldKeyErrorMessage from '../../field-key-error';
import type { PropertyErrors } from '../validation';
import { EnumValuesInput } from './enum-values-input';
import { useFieldTypeOptions } from './field-type-options';
import { getSchemaPatchForTypeChange } from './schema-state';
const INDENT_PX = 16;
type PropertyRowProps = {
  property: Property;
  onChange: (next: Property) => void;
  onDelete: () => void;
  /** False when this is the only child of an object/array<object> parent — schema requires ≥1 child. */
  canDelete: boolean;
  depth: number;
  errors?: PropertyErrors;
};
export const PropertyRow = t0 => {
  const $ = _c(94);
  const {
    property,
    onChange,
    onDelete,
    canDelete,
    depth,
    errors
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.task.schema"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const fieldTypeOptions = useFieldTypeOptions();
  let t2;
  if ($[1] !== property.type) {
    t2 = isObjectType(property.type);
    $[1] = property.type;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isObject = t2;
  let t3;
  if ($[3] !== onChange || $[4] !== property) {
    t3 = name => {
      onChange({
        ...property,
        name
      });
    };
    $[3] = onChange;
    $[4] = property;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const setName = t3;
  let t4;
  if ($[6] !== onChange || $[7] !== property) {
    t4 = description => {
      onChange({
        ...property,
        description: description || undefined
      });
    };
    $[6] = onChange;
    $[7] = property;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const setDescription = t4;
  let t5;
  if ($[9] !== onChange || $[10] !== property) {
    t5 = type => {
      const {
        patch
      } = getSchemaPatchForTypeChange(property, type);
      onChange({
        ...property,
        ...patch
      });
    };
    $[9] = onChange;
    $[10] = property;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const setType = t5;
  let t6;
  if ($[12] !== onChange || $[13] !== property) {
    t6 = values => {
      onChange({
        ...property,
        enumValues: values
      });
    };
    $[12] = onChange;
    $[13] = property;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const setEnumValues = t6;
  let t7;
  if ($[15] !== onChange || $[16] !== property) {
    t7 = (index, child) => {
      const children = [...(property.children ?? [])];
      children[index] = child;
      onChange({
        ...property,
        children
      });
    };
    $[15] = onChange;
    $[16] = property;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const updateChild = t7;
  let t8;
  if ($[18] !== onChange || $[19] !== property) {
    t8 = index_0 => {
      const children_0 = (property.children ?? []).filter((_, i) => i !== index_0);
      onChange({
        ...property,
        children: children_0
      });
    };
    $[18] = onChange;
    $[19] = property;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const removeChild = t8;
  let t9;
  if ($[21] !== onChange || $[22] !== property) {
    t9 = () => {
      onChange({
        ...property,
        children: [...(property.children ?? []), newProperty()]
      });
    };
    $[21] = onChange;
    $[22] = property;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const addChild = t9;
  const t10 = depth * INDENT_PX;
  let t11;
  if ($[24] !== t10) {
    t11 = {
      paddingLeft: t10
    };
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t) {
    t12 = t(_temp);
    $[26] = t;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const t13 = property.name;
  let t14;
  if ($[28] !== setName) {
    t14 = e => setName(e.target.value);
    $[28] = setName;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t) {
    t15 = t(_temp2);
    $[30] = t;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const t16 = !!errors?.name;
  const t17 = property.name === "";
  let t18;
  if ($[32] !== property.name || $[33] !== t12 || $[34] !== t14 || $[35] !== t15 || $[36] !== t16 || $[37] !== t17) {
    t18 = <div className="min-w-0 flex-1 md:w-32 md:flex-none"><TextInput aria-label={t12} size="compact" value={t13} onChange={t14} placeholder={t15} hasError={t16} autoFocus={t17} /></div>;
    $[32] = property.name;
    $[33] = t12;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t) {
    t19 = t(_temp3);
    $[39] = t;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  const t20 = property.description ?? "";
  let t21;
  if ($[41] !== setDescription) {
    t21 = e_0 => setDescription(e_0.target.value);
    $[41] = setDescription;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t) {
    t22 = t(_temp4);
    $[43] = t;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== t19 || $[46] !== t20 || $[47] !== t21 || $[48] !== t22) {
    t23 = <div className="order-last w-full min-w-0 md:order-none md:w-auto md:flex-1"><TextInput aria-label={t19} size="compact" value={t20} onChange={t21} placeholder={t22} /></div>;
    $[45] = t19;
    $[46] = t20;
    $[47] = t21;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  const t24 = `prop-${property.id}-type`;
  let t25;
  if ($[50] !== t) {
    t25 = t(_temp5);
    $[50] = t;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== setType) {
    t26 = value => setType(value as FieldType);
    $[52] = setType;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== fieldTypeOptions || $[55] !== property.type || $[56] !== t24 || $[57] !== t25 || $[58] !== t26) {
    t27 = <div className="w-32 shrink-0 md:w-40"><Select id={t24} aria-label={t25} size="compact" value={property.type} onChange={t26} options={fieldTypeOptions} /></div>;
    $[54] = fieldTypeOptions;
    $[55] = property.type;
    $[56] = t24;
    $[57] = t25;
    $[58] = t26;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== t) {
    t28 = t(_temp6);
    $[60] = t;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  const t29 = !canDelete;
  let t30;
  if ($[62] !== onDelete || $[63] !== t28 || $[64] !== t29) {
    t30 = <IconButton aria-label={t28} icon={IcoTrash16} destructive={true} disabled={t29} onClick={onDelete} />;
    $[62] = onDelete;
    $[63] = t28;
    $[64] = t29;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  let t31;
  if ($[66] !== t18 || $[67] !== t23 || $[68] !== t27 || $[69] !== t30) {
    t31 = <div className="flex flex-wrap items-start gap-2 md:flex-nowrap">{t18}{t23}{t27}{t30}</div>;
    $[66] = t18;
    $[67] = t23;
    $[68] = t27;
    $[69] = t30;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  const t32 = errors?.name;
  let t33;
  if ($[71] !== t32) {
    t33 = <FieldKeyErrorMessage error={t32} />;
    $[71] = t32;
    $[72] = t33;
  } else {
    t33 = $[72];
  }
  let t34;
  if ($[73] !== errors?.enumValues || $[74] !== property.enumValues || $[75] !== property.type || $[76] !== setEnumValues) {
    t34 = isEnumType(property.type) && <div style={{
      paddingLeft: INDENT_PX
    }}><EnumValuesInput values={property.enumValues ?? []} onChange={setEnumValues} hasError={!!errors?.enumValues} /></div>;
    $[73] = errors?.enumValues;
    $[74] = property.enumValues;
    $[75] = property.type;
    $[76] = setEnumValues;
    $[77] = t34;
  } else {
    t34 = $[77];
  }
  let t35;
  if ($[78] !== addChild || $[79] !== depth || $[80] !== errors?.children || $[81] !== errors?.noChildren || $[82] !== isObject || $[83] !== property.children || $[84] !== removeChild || $[85] !== t || $[86] !== updateChild) {
    t35 = isObject && <div className="flex flex-col gap-2 pb-2">{(property.children ?? []).map((child_0, index_1, arr) => <PropertyRow key={child_0.id} property={child_0} onChange={next => updateChild(index_1, next)} onDelete={() => removeChild(index_1)} canDelete={arr.length > 1} depth={depth + 1} errors={errors?.children?.[child_0.id]} />)}<div className="flex items-center gap-2" style={{
        paddingLeft: (depth + 1) * INDENT_PX
      }}><LinkButton variant="caption-3" iconComponent={IcoPlusSmall16} iconPosition="left" onClick={addChild}>{t(_temp7)}</LinkButton>{errors?.noChildren === "empty" && <span className="text-caption-1 text-error">{t(_temp8)}</span>}</div></div>;
    $[78] = addChild;
    $[79] = depth;
    $[80] = errors?.children;
    $[81] = errors?.noChildren;
    $[82] = isObject;
    $[83] = property.children;
    $[84] = removeChild;
    $[85] = t;
    $[86] = updateChild;
    $[87] = t35;
  } else {
    t35 = $[87];
  }
  let t36;
  if ($[88] !== t11 || $[89] !== t31 || $[90] !== t33 || $[91] !== t34 || $[92] !== t35) {
    t36 = <div className="flex flex-col gap-2" style={t11}>{t31}{t33}{t34}{t35}</div>;
    $[88] = t11;
    $[89] = t31;
    $[90] = t33;
    $[91] = t34;
    $[92] = t35;
    $[93] = t36;
  } else {
    t36 = $[93];
  }
  return t36;
};
function _temp($) {
  return $.property.nameAria;
}
function _temp2($_0) {
  return $_0.property.namePlaceholder;
}
function _temp3($_1) {
  return $_1.property.descriptionAria;
}
function _temp4($_2) {
  return $_2.property.descriptionPlaceholder;
}
function _temp5($_3) {
  return $_3.property.typeAria;
}
function _temp6($_4) {
  return $_4.property.removeAria;
}
function _temp7($_5) {
  return $_5.addProperty;
}
function _temp8($_6) {
  return $_6.errors.noChildren;
}